/* ==========================================================================
   Milo by Son & Bear — theme interactions
   Foundational modules: announcement bar, mobile nav, scroll reveal,
   accordions, cart drawer + AJAX add-to-cart. The configurator custom
   element (<milo-configurator>) is registered in configurator.js.
   ========================================================================== */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---- Analytics bus with buffer + replay (providers attach in analytics.liquid) --- */
  const Analytics = (window.MiloAnalytics = window.MiloAnalytics || {});
  Analytics._debug = Analytics._debug || document.documentElement.dataset.analyticsDebug === 'true';
  Analytics._handlers = Analytics._handlers || [];
  Analytics._buffer = Analytics._buffer || [];
  Analytics.track = function (event, payload) {
    if (this._debug) console.info('[milo:analytics]', event, payload || {});
    this._buffer.push([event, payload]);
    this._handlers.forEach((fn) => { try { fn(event, payload); } catch (e) {} });
  };
  // onTrack replays already-buffered events so late-attaching providers miss nothing.
  Analytics.onTrack = function (fn) {
    this._handlers.push(fn);
    this._buffer.forEach(([e, p]) => { try { fn(e, p); } catch (err) {} });
  };

  /* ---- Money helper ------------------------------------------------------ */
  const money = (cents, symbol = 'S$') =>
    symbol + (cents / 100).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  window.MiloMoney = money;

  /* ---- Announcement bar -------------------------------------------------- */
  function initAnnouncement() {
    const bar = $('[data-announcement]');
    if (!bar) return;
    if (sessionStorage.getItem('milo:announce-dismissed') === '1') { bar.hidden = true; return; }

    const items = $$('.announcement__item', bar);
    if (items.length > 1) {
      let i = 0;
      items[0].classList.add('is-active');
      const secs = parseInt(bar.dataset.rotate || '5', 10);
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, secs * 1000);
    }
    const close = $('[data-announcement-close]', bar);
    if (close) close.addEventListener('click', () => {
      bar.hidden = true;
      sessionStorage.setItem('milo:announce-dismissed', '1');
    });
  }

  /* ---- Mobile nav -------------------------------------------------------- */
  function initNav() {
    const toggle = $('[data-nav-toggle]');
    const nav = $('[data-mobile-nav]');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      nav.hidden = open;
    });
  }

  /* ---- Scroll reveal ----------------------------------------------------- */
  function initReveal() {
    const els = $$('.reveal');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    els.forEach((el) => io.observe(el));
  }

  /* ---- Accordions -------------------------------------------------------- */
  function initAccordions() {
    $$('.accordion__trigger').forEach((trigger) => {
      const panel = trigger.nextElementSibling;
      if (!panel) return;
      trigger.setAttribute('aria-expanded', trigger.getAttribute('aria-expanded') || 'false');
      trigger.addEventListener('click', () => {
        const open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!open));
        panel.style.height = open ? '0px' : panel.scrollHeight + 'px';
      });
    });
  }

  /* ---- Cart drawer + AJAX ------------------------------------------------ */
  const Cart = (window.MiloCart = {
    drawer: null,
    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('scroll-locked');
    },
    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('scroll-locked');
    },
    async add(id, quantity = 1, properties = null) {
      const body = { items: [{ id, quantity }] };
      if (properties) body.items[0].properties = properties;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('add-to-cart failed: ' + res.status);
      const line = await res.json();
      await this.refresh();
      this.open();
      Analytics.track('add_to_cart', { id, quantity, line });
      return line;
    },
    async change(key, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('cart change failed');
      await this.refresh();
    },
    async refresh() {
      // Re-render the drawer body via the Section Rendering API when available,
      // else fall back to fetching /cart.js and updating counts.
      try {
        const res = await fetch('/?sections=cart-drawer');
        if (res.ok) {
          const data = await res.json();
          if (data['cart-drawer']) {
            const body = $('[data-cart-body]');
            if (body) body.innerHTML = extractSection(data['cart-drawer']);
          }
        }
      } catch (e) { /* non-fatal */ }
      const cart = await (await fetch('/cart.js')).json();
      updateCartCount(cart.item_count);
    },
  });

  function extractSection(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inner = doc.querySelector('[data-cart-body]');
    return inner ? inner.innerHTML : html;
  }

  function updateCartCount(count) {
    const el = $('[data-cart-count]');
    if (!el) return;
    el.textContent = count;
    el.hidden = count === 0;
  }

  function initCart() {
    Cart.drawer = $('[data-cart-drawer]');
    if (!Cart.drawer) return;

    // Open drawer instead of navigating to /cart (unless empty)
    $$('[data-cart-toggle]').forEach((btn) =>
      btn.addEventListener('click', (e) => { e.preventDefault(); Cart.refresh(); Cart.open(); })
    );
    $$('[data-cart-close]').forEach((btn) => btn.addEventListener('click', () => Cart.close()));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') Cart.close(); });

    // Delegated quantity controls inside the drawer
    Cart.drawer.addEventListener('click', (e) => {
      const line = e.target.closest('[data-line-key]');
      if (!line) return;
      const key = line.dataset.lineKey;
      const input = $('[data-qty-input]', line);
      if (e.target.closest('[data-qty-up]')) Cart.change(key, parseInt(input.value, 10) + 1);
      if (e.target.closest('[data-qty-down]')) Cart.change(key, Math.max(0, parseInt(input.value, 10) - 1));
    });
    Cart.drawer.addEventListener('change', (e) => {
      const line = e.target.closest('[data-line-key]');
      if (line && e.target.matches('[data-qty-input]')) {
        Cart.change(line.dataset.lineKey, Math.max(0, parseInt(e.target.value, 10) || 0));
      }
    });
  }

  /* ---- Generic quantity steppers (outside the cart drawer) --------------- */
  function initQtyFields() {
    $$('[data-qty-field]').forEach((field) => {
      const input = $('[data-qty-input]', field);
      if (!input) return;
      const clamp = (n) => Math.max(parseInt(input.min || '1', 10), n || 0);
      $('[data-qty-up]', field)?.addEventListener('click', () => { input.value = clamp(parseInt(input.value, 10) + 1); });
      $('[data-qty-down]', field)?.addEventListener('click', () => { input.value = clamp(parseInt(input.value, 10) - 1); });
    });
  }

  /* ---- PDP: buy box, gallery, sticky bar, lightbox ----------------------- */
  function initPdp() {
    const pdp = $('[data-pdp]');
    if (!pdp) return;
    const isDemo = pdp.dataset.demo === 'true';

    const priceEls = $$('[data-price], [data-price-mirror]', pdp);
    const atcLabel = $('[data-atc-label]', pdp);
    const atcPrice = $('[data-atc-price]', pdp);
    const atcBtn = $('[data-atc]', pdp);
    const atcMirror = $('[data-atc-mirror]', pdp);
    const unavailable = $('[data-unavailable]', pdp);
    const stickyTitle = $('.pdp__stickybar-title', pdp);
    const financeAmt = $('[data-financing-amount]', pdp);
    const form = $('[data-product-form]', pdp);
    const idInput = $('[data-variant-id]', pdp);
    let current = { available: false, price: null, currency: 'S$' };

    // React to configurator changes
    pdp.addEventListener('milo:variant-change', (e) => {
      const d = e.detail;
      current = d;
      const sym = d.currency || 'S$';
      if (d.price != null) {
        priceEls.forEach((el) => (el.textContent = money(d.price, sym)));
        if (atcPrice) atcPrice.textContent = '· ' + money(d.price, sym);
        if (financeAmt) financeAmt.textContent = money(Math.round(d.price / 3), sym).replace(sym, '');
      }
      if (stickyTitle) stickyTitle.textContent = 'Milo · ' + (d.size || '');
      const buyable = d.purchasable && !isDemo;
      [atcBtn, atcMirror].forEach((b) => { if (b) { b.disabled = !buyable; b.classList.toggle('is-disabled', !buyable); } });
      if (atcLabel) atcLabel.textContent = isDemo ? 'Preview only' : (buyable ? 'Add to cart' : 'Made to order');
      if (unavailable) unavailable.hidden = buyable || isDemo;
      const dimensions = $('[data-selected-dimensions]', pdp);
      if (dimensions) dimensions.textContent = d.dimensionsLabel || '';
    });

    // Gallery swaps carry the complete responsive state. A request gate prevents
    // late preloads from replacing a newer variant or lifestyle choice.
    const mainImg = $('[data-gallery-main]', pdp);
    const mobileSource = $('[data-gallery-mobile]', pdp);
    const fallbackGalleryState = {
      variantState(detail = {}) {
        if (!detail.src) return null;
        return {
          src: detail.src,
          srcset: '',
          mobileSrcset: '',
          sizes: '',
          alt: detail.alt || 'Milo modular sofa',
          position: '50% 50%',
          fit: 'contain',
        };
      },
      lifestyleState(data = {}) {
        if (!data.src) return null;
        return {
          src: data.src,
          srcset: data.srcset || '',
          mobileSrcset: data.mobileSrcset || '',
          sizes: data.sizes || '(min-width: 901px) 54vw, 100vw',
          alt: data.alt || 'Milo modular sofa in a home',
          position: data.position || '50% 50%',
          fit: 'cover',
        };
      },
      zoomSrcFor(src) {
        const s = typeof src === 'string' ? src.trim() : '';
        if (!s) return '';
        return /[?&]width=\d+/.test(s) ? s.replace(/([?&]width=)\d+/, '$12048') : s;
      },
    };
    const galleryState = window.MiloGalleryState || fallbackGalleryState;
    let galleryRequest = 0;
    const requestGate = window.MiloGalleryState?.RequestGate
      ? new window.MiloGalleryState.RequestGate()
      : {
          next: () => ++galleryRequest,
          isCurrent: (token) => token === galleryRequest,
        };

    function applyGalleryState(state) {
      if (!mainImg || !state) return;
      if (mobileSource) {
        if (state.mobileSrcset) mobileSource.setAttribute('srcset', state.mobileSrcset);
        else mobileSource.removeAttribute('srcset');
      }
      if (state.srcset) mainImg.setAttribute('srcset', state.srcset);
      else mainImg.removeAttribute('srcset');
      if (state.sizes) mainImg.setAttribute('sizes', state.sizes);
      else mainImg.removeAttribute('sizes');
      mainImg.src = state.src;
      mainImg.alt = state.alt;
      mainImg.style.objectPosition = state.position;
      mainImg.dataset.galleryFit = state.fit;
      mainImg.dataset.zoomSrc = galleryState.zoomSrcFor ? galleryState.zoomSrcFor(state.src) : '';
      mainImg.style.opacity = '1';
    }

    pdp.addEventListener('milo:image-change', (e) => {
      const state = galleryState.variantState(e.detail);
      if (!mainImg || !state) return;
      const token = requestGate.next();
      mainImg.style.opacity = '0';
      const pre = new Image();
      pre.onload = () => { if (requestGate.isCurrent(token)) applyGalleryState(state); };
      pre.onerror = () => { if (requestGate.isCurrent(token)) mainImg.style.opacity = '1'; };
      pre.src = state.src;
      $$('[data-gallery-thumb]', pdp).forEach((t) => t.classList.remove('is-active'));
    });

    // Thumbnail clicks
    $$('[data-gallery-thumb]', pdp).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const state = galleryState.lifestyleState(thumb.dataset);
        if (!state) return;
        requestGate.next();
        applyGalleryState(state);
        $$('[data-gallery-thumb]', pdp).forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    // Lightbox — true zoom + pan. The image opens fit-to-screen; a click/tap
    // toggles a magnified state, and dragging pans the enlarged image. On touch
    // the container also scrolls, so pinch/scroll reveals detail.
    const lightbox = $('[data-lightbox]', pdp);
    const lightboxImg = $('[data-lightbox-img]', pdp);
    const lightboxStage = $('[data-lightbox-stage]', pdp) || lightboxImg?.parentElement;

    // dragState is only active between pointerdown and pointerup on a zoomed
    // image; wasDragged marks that the just-finished pointer sequence panned, so
    // the trailing click does not toggle zoom. Both are cleared whenever the
    // lightbox is (re)opened/closed via resetZoom so a stale pan can never leak
    // into a later interaction.
    let dragState = null;
    let wasDragged = false;

    function resetZoom() {
      if (!lightboxImg) return;
      lightboxImg.classList.remove('is-zoomed');
      if (lightboxStage) { lightboxStage.scrollLeft = 0; lightboxStage.scrollTop = 0; }
      dragState = null;
      wasDragged = false;
    }

    $('[data-gallery-zoom]', pdp)?.addEventListener('click', () => {
      if (lightbox && lightboxImg && mainImg) {
        // Zoom the crop actually on screen: currentSrc reflects the resolved
        // <picture> source (the mobile <source> under 600px, else the desktop
        // <img>), so a mobile crop stays a mobile crop. Upgrade it to 2048.
        const shown = mainImg.currentSrc || mainImg.src;
        const hires = galleryState.zoomSrcFor ? galleryState.zoomSrcFor(shown) : shown;
        lightboxImg.src = hires || mainImg.dataset.zoomSrc || shown;
        lightboxImg.alt = mainImg.alt;
        resetZoom();
        lightbox.showModal?.();
      }
    });

    // Click/tap toggles magnification, centred on the pointer position.
    lightboxImg?.addEventListener('click', (e) => {
      // Swallow the click that ends a drag-pan; consume the one-shot flag.
      if (wasDragged) { wasDragged = false; return; }
      if (lightboxImg.classList.contains('is-zoomed')) { resetZoom(); return; }
      if (!lightboxStage) { lightboxImg.classList.add('is-zoomed'); return; }
      // Capture where the click lands on the fit-to-screen image BEFORE magnifying,
      // then scroll the enlarged image so that same point is centred in view.
      const rect = lightboxImg.getBoundingClientRect();
      const relX = rect.width ? Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1) : 0.5;
      const relY = rect.height ? Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1) : 0.5;
      lightboxImg.classList.add('is-zoomed');
      requestAnimationFrame(() => {
        lightboxStage.scrollLeft = relX * (lightboxStage.scrollWidth - lightboxStage.clientWidth);
        lightboxStage.scrollTop = relY * (lightboxStage.scrollHeight - lightboxStage.clientHeight);
      });
    });

    // Pointer drag pans while zoomed (mouse + touch via Pointer Events).
    lightboxImg?.addEventListener('pointerdown', (e) => {
      if (!lightboxImg.classList.contains('is-zoomed') || !lightboxStage) return;
      wasDragged = false;
      dragState = { x: e.clientX, y: e.clientY, left: lightboxStage.scrollLeft, top: lightboxStage.scrollTop };
      try { lightboxImg.setPointerCapture?.(e.pointerId); } catch (_) {}
    });
    lightboxImg?.addEventListener('pointermove', (e) => {
      if (!dragState || !lightboxStage) return;
      const dx = e.clientX - dragState.x;
      const dy = e.clientY - dragState.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) wasDragged = true;
      lightboxStage.scrollLeft = dragState.left - dx;
      lightboxStage.scrollTop = dragState.top - dy;
    });
    // Clear dragState first so a later hover/move can never pan, even if the
    // pointer-capture release throws (e.g. an already-released pointer).
    const endDrag = (e) => {
      if (!dragState) return;
      dragState = null;
      try { lightboxImg?.releasePointerCapture?.(e.pointerId); } catch (_) {}
    };
    lightboxImg?.addEventListener('pointerup', endDrag);
    lightboxImg?.addEventListener('pointercancel', endDrag);

    $('[data-lightbox-close]', pdp)?.addEventListener('click', () => lightbox?.close?.());
    lightbox?.addEventListener('click', (e) => { if (e.target === lightbox || e.target === lightboxStage) lightbox.close(); });
    lightbox?.addEventListener('close', resetZoom);

    // Add to cart (AJAX)
    async function submitAdd(redirectToCheckout) {
      if (isDemo || !idInput || !idInput.value) return;
      const qty = parseInt($('[data-qty-input]', form)?.value || '1', 10);
      // Collect all active line-item properties (e.g. Colour)
      const properties = {};
      form.querySelectorAll('[name^="properties["]').forEach((el) => {
        if (el.disabled || !el.value) return;
        const m = el.name.match(/^properties\[(.+)\]$/);
        if (m) properties[m[1]] = el.value;
      });
      const props = Object.keys(properties).length ? properties : null;
      Analytics.track('add_to_cart_click', { id: idInput.value, qty, price: current.price, properties: props });
      try {
        [atcBtn, atcMirror].forEach((b) => b && (b.disabled = true));
        await Cart.add(idInput.value, qty, props);
        if (redirectToCheckout) window.location.href = '/checkout';
      } catch (err) {
        console.error(err);
        if (unavailable) { unavailable.hidden = false; unavailable.textContent = 'Something went wrong — please try again.'; }
      } finally {
        [atcBtn, atcMirror].forEach((b) => b && (b.disabled = false));
      }
    }
    form?.addEventListener('submit', (e) => { e.preventDefault(); submitAdd(false); });
    $('[data-buy-now]', pdp)?.addEventListener('click', () => submitAdd(true));
    atcMirror?.addEventListener('click', () => submitAdd(false));

    // ViewContent for analytics
    Analytics.track('view_content', { product: 'milo-modular-sofa' });

    // Sticky mobile bar reveal when buy box scrolls past
    const stickyBar = $('[data-sticky-bar]', pdp);
    if (stickyBar && atcBtn && 'IntersectionObserver' in window) {
      const mobileSticky = window.matchMedia('(max-width: 900px)');
      let atcOutOfView = false;
      const updateSticky = () => {
        const visible = mobileSticky.matches && atcOutOfView;
        stickyBar.classList.toggle('is-visible', visible);
        stickyBar.setAttribute('aria-hidden', String(!visible));
        if (atcMirror) atcMirror.tabIndex = visible ? 0 : -1;
      };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          atcOutOfView = !entry.isIntersecting && entry.boundingClientRect.top < rootTop;
          updateSticky();
        });
      }, { rootMargin: '-20% 0px 0px 0px' });
      io.observe(atcBtn);
      if (typeof mobileSticky.addEventListener === 'function') {
        mobileSticky.addEventListener('change', updateSticky);
      } else {
        mobileSticky.addListener(updateSticky);
      }
    }
  }

  /* ---- Boot -------------------------------------------------------------- */
  function boot() {
    initAnnouncement();
    initNav();
    initReveal();
    initAccordions();
    initCart();
    initQtyFields();
    initPdp();
    document.dispatchEvent(new CustomEvent('milo:ready'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
